import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { getMe, linkPhoneNumber, unlinkPhoneNumber, getPhoneVerificationStatus } from '../../lib/api';
import { PhoneCodeVerification } from './PhoneCodeVerification';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Copy, Check, Mail, Phone, Plus, X, Loader2 } from 'lucide-react';

const ReceiveMoneyTab = ({ user }) => {
  const [copied, setCopied] = useState({ email: false, phone: false });
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [showLinkPhone, setShowLinkPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [linking, setLinking] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState({ channel_available: false, phone_verified: false });

  useEffect(() => {
    getPhoneVerificationStatus()
      .then(({ data }) => {
        setPhoneStatus(data);
        if (data.phone_number) setPhoneNumber(data.phone_number);
      })
      .catch(() => {});
  }, []);

  const handleVerified = (verifiedPhone) => {
    setPhoneNumber(verifiedPhone);
    setPhoneStatus((s) => ({ ...s, phone_verified: true }));
    setShowLinkPhone(false);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [type]: true });
    toast.success(`${type === 'email' ? 'Email' : 'Phone number'} copied!`);
    setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
  };

  const handleLinkPhone = async () => {
    if (!newPhone || newPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setLinking(true);
    try {
      const result = await linkPhoneNumber(newPhone);
      setPhoneNumber(result.data.phone_number);
      setShowLinkPhone(false);
      setNewPhone('');
      
      if (result.data.pending_transfers_completed > 0) {
        toast.success(`Phone linked! ${result.data.pending_transfers_completed} pending transfer(s) completed.`);
      } else {
        toast.success('Phone number linked successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link phone number');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkPhone = async () => {
    try {
      await unlinkPhoneNumber();
      setPhoneNumber('');
      setPhoneStatus((s) => ({ ...s, phone_verified: false }));
      toast.success('Phone number unlinked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink phone number');
    }
  };

  return (
    <div className="space-y-6" data-testid="receive-money-tab">
      {/* Email Receive */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-600" />
            Receive via Email
          </CardTitle>
          <CardDescription>Share your email to receive funds instantly</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-6 bg-muted rounded-xl text-center">
            <p className="text-sm text-muted-foreground mb-2">Your ParachPay Email</p>
            <p className="text-xl font-mono font-semibold" data-testid="user-email">{user?.email}</p>
          </div>

          <Button
            onClick={() => copyToClipboard(user?.email, 'email')}
            variant="outline"
            className="w-full h-12 rounded-xl"
            data-testid="copy-email-btn"
          >
            {copied.email ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copy Email Address
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Phone Receive */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            Receive via Phone
          </CardTitle>
          <CardDescription>Link your phone to receive money by phone number</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {phoneNumber ? (
            <>
              <div className="p-6 bg-muted rounded-xl text-center">
                <p className="text-sm text-muted-foreground mb-2">Your Linked Phone Number</p>
                <p className="text-xl font-mono font-semibold" data-testid="user-phone">{phoneNumber}</p>
                <Badge variant="outline" className={`mt-2 ${phoneStatus.phone_verified ? 'text-emerald-600 border-emerald-600/40' : 'text-amber-600 border-amber-600/40'}`} data-testid="phone-verified-badge">
                  {phoneStatus.phone_verified ? 'Verified' : 'Not verified'}
                </Badge>
              </div>
              {!phoneStatus.phone_verified && phoneStatus.channel_available && (
                <Dialog open={showLinkPhone} onOpenChange={setShowLinkPhone}>
                  <DialogTrigger asChild>
                    <Button className="w-full h-12 rounded-xl" data-testid="verify-phone-btn">Verify this number</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Verify Phone Number</DialogTitle>
                    </DialogHeader>
                    <PhoneCodeVerification initialPhone={phoneNumber} onVerified={handleVerified} />
                  </DialogContent>
                </Dialog>
              )}
              {!phoneStatus.phone_verified && phoneStatus.channel_available && (
                <p className="text-xs text-muted-foreground" data-testid="phone-unverified-note">Verify your number to receive money by phone.</p>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => copyToClipboard(phoneNumber, 'phone')}
                  variant="outline"
                  className="flex-1 h-12 rounded-xl"
                  data-testid="copy-phone-btn"
                >
                  {copied.phone ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Phone
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleUnlinkPhone}
                  variant="outline"
                  className="h-12 rounded-xl text-red-600 hover:bg-red-50"
                  data-testid="unlink-phone-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Unlink
                </Button>
              </div>
            </>
          ) : (
            <Dialog open={showLinkPhone} onOpenChange={setShowLinkPhone}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full h-12 rounded-xl" data-testid="link-phone-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Link Phone Number
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">Link Phone Number</DialogTitle>
                </DialogHeader>
                {phoneStatus.channel_available ? (
                  <PhoneCodeVerification onVerified={handleVerified} />
                ) : (
                <>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="link-phone">Phone Number (with country code)</Label>
                    <Input
                      id="link-phone"
                      type="tel"
                      placeholder="+1 555 123 4567"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      className="h-12 rounded-xl font-mono"
                      data-testid="link-phone-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Include your country code (e.g., +1 for US)
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleLinkPhone}
                    disabled={linking}
                    className="w-full rounded-xl"
                    data-testid="confirm-link-phone-btn"
                  >
                    {linking ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Linking...
                      </>
                    ) : (
                      'Link Phone Number'
                    )}
                  </Button>
                </DialogFooter>
                </>
                )}
              </DialogContent>
            </Dialog>
          )}
        </CardContent>
      </Card>

      {/* How to Receive */}
      <Card className="bg-emerald-500/5 border-emerald-500/20">
        <CardContent className="pt-6">
          <p className="font-medium text-emerald-700 dark:text-emerald-400 mb-3">How to receive funds</p>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-600 flex items-center justify-center text-xs font-medium flex-shrink-0">1</span>
              <span>Share your email or phone number with the sender</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-600 flex items-center justify-center text-xs font-medium flex-shrink-0">2</span>
              <span>They can send any supported currency to you</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-600 flex items-center justify-center text-xs font-medium flex-shrink-0">3</span>
              <span>Funds appear instantly in your wallet (no fees for receiving)</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReceiveMoneyTab;
